import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';

import { useApplicationContext } from 'ApplicationContext';
import SettingsPageHeader from './SettingsPageHeader';
import { settingsApi } from './settingsApi';

/*
 * Lokal statt aus dem erzeugten Client: `src/apiClient/Api.ts` entsteht aus dem Swagger eines
 * LAUFENDEN Servers (`npm run generate-api`) und kennt die Endpunkte noch nicht.
 */
interface NotificationSettingsDto {
    teamsWebhookUrl: string;
}

interface NotificationTestResult {
    success: boolean;
    message: string;
}

/**
 * Benachrichtigungs-Einstellungen: die Webhook-URL des Teams-Kanals, in den das Support-System
 * meldet — derzeit neue Rückrufbitten.
 *
 * Bewusst ein Webhook statt eines Bots: keine App-Registrierung, keine Berechtigungen — die URL
 * entsteht in Teams über einen „Workflows"-Flow, die Anleitung steht direkt auf der Seite.
 *
 * Deutsch fest verdrahtet — wie die Geschwisterseiten.
 */
export const NotificationSettings: React.FC = () => {
    const context = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    // Über request() statt api.instance — sonst fehlt der Authorization-Header.
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [url, setUrl] = useState('');
    const [savedUrl, setSavedUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState<NotificationTestResult | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get<NotificationSettingsDto>('/NotificationSettings/Settings');
            setUrl(r.data.teamsWebhookUrl ?? '');
            setSavedUrl(r.data.teamsWebhookUrl ?? '');
        } catch (e) {
            context.apiError(e);
        } finally {
            setLoading(false);
        }
    }, [context, http]);

    useEffect(() => {
        void load();
    }, [load]);

    const dirty = url.trim() !== savedUrl.trim();

    const problems = useMemo(() => {
        const trimmed = url.trim();
        if (trimmed && !/^https:\/\//i.test(trimmed)) {
            return ['Die Webhook-URL muss mit https:// beginnen.'];
        }
        return [];
    }, [url]);

    const save = async (): Promise<void> => {
        setBusy(true);
        try {
            const r = await http.put<NotificationSettingsDto>(
                '/NotificationSettings/Settings', { teamsWebhookUrl: url.trim() });
            setUrl(r.data.teamsWebhookUrl ?? '');
            setSavedUrl(r.data.teamsWebhookUrl ?? '');
            setTest(null);
            enqueueSnackbar('Gespeichert — greift ohne Neustart.', { variant: 'success' });
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    const runTest = async (): Promise<void> => {
        setTesting(true);
        setTest(null);
        try {
            const r = await http.post<NotificationTestResult>('/NotificationSettings/TestMessage');
            setTest(r.data);
        } catch (e) {
            context.apiError(e);
        } finally {
            setTesting(false);
        }
    };

    return (
        <Grid size={12}>
            <SettingsPageHeader sectionKey="notifications"/>
            {loading && <LinearProgress sx={{ mb: 2 }}/>}

            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Webhook in Teams anlegen
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Kein Bot, keine Administrator-Rechte: jedes Mitglied des Kanals kann den
                                Webhook über die Teams-„Workflows" anlegen. Der Flow läuft danach unter dem
                                Konto, das ihn erstellt hat — am besten von einem Konto anlegen lassen, das
                                dauerhaft bestehen bleibt.
                            </Typography>
                            <List dense sx={{ py: 0 }}>
                                <ListItem sx={{ py: 0.25 }}>
                                    <ListItemText
                                        primary="1. In Teams den Ziel-Kanal öffnen → „⋯“ neben dem Kanalnamen → Workflows"
                                        secondary="Alternativ: linke Seitenleiste → Apps → „Workflows“."/>
                                </ListItem>
                                <ListItem sx={{ py: 0.25 }}>
                                    <ListItemText
                                        primary="2. Vorlage „Auf Webhookanforderung in einem Kanal posten“ wählen"
                                        secondary="Team und Kanal bestätigen — ein Standardkanal, kein privater."/>
                                </ListItem>
                                <ListItem sx={{ py: 0.25 }}>
                                    <ListItemText
                                        primary="3. Die angezeigte Webhook-URL kopieren und unten eintragen"
                                        secondary="Die URL ist ein Geheimnis: wer sie kennt, kann in den Kanal posten."/>
                                </ListItem>
                            </List>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <TextField fullWidth size="small" label="Teams-Webhook-URL"
                                       value={url}
                                       onChange={(e) => setUrl(e.target.value)}
                                       placeholder="https://prod-….westeurope.logic.azure.com/workflows/…"
                                       helperText="Leer lassen schaltet die Teams-Meldungen ab — Rückrufbitten funktionieren dann trotzdem, nur ohne Benachrichtigung."/>

                            {problems.length > 0 && (
                                <Alert severity="warning">
                                    {problems.map((problem) => <div key={problem}>{problem}</div>)}
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                <Button variant="contained" onClick={save}
                                        disabled={busy || loading || problems.length > 0}>
                                    {busy ? 'Speichere …' : 'Speichern'}
                                </Button>
                                <Tooltip title={dirty ? 'Das Formular hat ungespeicherte Änderungen — der Test prüft den gespeicherten Stand.' : ''}>
                                    <span>
                                        <Button variant="outlined" onClick={runTest}
                                                disabled={testing || loading}
                                                color={dirty ? 'warning' : 'primary'}
                                                startIcon={<FontAwesomeIcon icon={faPaperPlane}/>}>
                                            {testing ? 'Sende …' : 'Testnachricht senden'}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button variant="text" onClick={load} disabled={busy || loading}>
                                    Verwerfen
                                </Button>
                            </Stack>

                            {test && (
                                <Alert severity={test.success ? 'success' : 'error'}>
                                    <AlertTitle>{test.success ? 'Zugestellt' : 'Fehlgeschlagen'}</AlertTitle>
                                    {test.message}
                                </Alert>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default NotificationSettings;
